import localforage from "localforage";

import { SortTypes } from "./enums";
import RemarkType from "./model/RemarkType";
import SidebarTabType from "./model/SidebarTabType";
import { ThemePreference } from "./theme";

const VERSIONS_SIDEBAR_INITIAL_WIDTH = 260;

const USER_DEFAULTS = {
    theme: ThemePreference.System,
    notificationStatus: false,
    notificationBannerStatus: true,
    projectSortType: SortTypes.CREATED,
    styleguidesUsed: false,
    tabTutorialSeen: false,
    noVectorAssetInfoHidden: false,
    sourceFileInfoHidden: false,
    lastOrganization: "user",
    pinnedBarrelOnce: false,
    dashboardMemberSortKey: { field: "role", reverse: true },
    componentVariantsOnboardingDialogSeen: false,
    componentVariantsOnboardingHintboxSeen: false,
    componentVariantViewSeen: false,
    styleguidePagesOnboardingTeyzeSeen: false,
    numberOfLayerComponentViews: 0,
    resolvedDotsTutorialSeen: false,
    nestedScreenSectionsOnboardingSeen: false,
    tagsEmptyOnboardingSeen: false,
    tagGroupsEmptyOnboardingSeen: false,
    noScreensDesignToolSelection: null,
    flowLinkInfoDismissed: false,
    windowsAppsNewReleasePointReminderOpen: false,
    densityMismatchWarningHidden: false,
    shouldPromoteSpacingSectionDescriptionEditButton: true,
    shouldPromoteExportDesignTokens: true,
    shouldDisplayInvitedUserHintbox: true,
    shouldDisplayPersonalWorkspaceHintbox: true,
    shouldDisplayFreeTrialOnboardingHintbox: true,
    shouldDisplayFreeTrialPromotionDialog: true,
    shouldDisplayDashboardOnboardingHintbox: true,
    shouldDisplayAddToFlowOnboarding: true,
    shouldDisplayAddToFlowTooltip: true, // This is a legacy, read-only pref value.
    flowsTabTooltipSeen: false,
    componentVersionsNewBadgeMenuSeen: false,
    recentlyLoadedBarrels: { projects: [], styleguides: [] },
    upgradeForMoreRolesHintboxHidden: false,
    offerReminderNextDate: null,
    activitiesSidebarWidth: 260,
    activitiesShowProjectsWithUpdatesOnly: true,
    bannedSuggestedResourcesByBarrel: {},
    storybookIntegrationPromotionSeen: false,
    highlightComponentsPromotionSeen: false, // This is a legacy, read-only pref value.
    linkToStyleguidePromotionSeen: false,
    stageModeFirstTimeLandingHidden: false,
    flowOnboardingConnectorHidden: false,
    events: {},
    dismissedTooltips: {},
    guidanceLastDisplayDates: {},
    requestApprovalPopupSeen: false,
    approvalsReminderDismissCount: 0,
    approvalsReminderDismissUntil: null,
    showSVGContent: false,
    newApprovalCreatedFeedbackCounter: 0,
    createSectionPromotionCounter: 0,
    addTagPromotionCounter: 0,
    requestApprovalPromotionCounter: 0,
    valentinesDates: {}
};

const BARREL_DEFAULTS = {
    // Common
    assetDensityScalePrefs: null, // Calculated wrt project type
    assetNamingConvention: "default",
    dashboardSidebarWidth: 280,
    inspectableSidebarWidth: 320,
    styleguideLeftSidebarWidth: 260,
    styleguideRightSidebarWidth: 280,
    lastNoteColorUsed: "yellow",
    lastConnectorEndStyleUsed: "arrow",
    lastShapeColorUsed: "teflon",
    lastNoteColorUsedForFlow: "yellow",
    flowGroupPanelWidth: 192,
    layoutGridEnabled: false,
    noResourceInfoDismissed: false,
    noteVisibility: true,
    noteStatusFilter: "open",
    annotationStatusFilter: "all",
    noteColorFilter: null,
    collapsedSections: {},
    showOptimizedSVG: true,
    showOptimizedPNG: true,
    showOptimizedJPG: true,
    colorFormatPreference: "auto",
    slackReconnectionAuntieSeen: false,
    versionsSidebarWidth: VERSIONS_SIDEBAR_INITIAL_WIDTH,
    zoomLevel: 1.0,
    fixedThumbnailSize: false,
    disabledExtensions: [],
    onboardingLayerCompleted: false,
    onboardingDotCompleted: false,
    onboardingVersionsCompleted: false,
    onboardingAssetsCompleted: false,
    screenOnboardingStep: null,
    screenOnboardingFlowHintboxHidden: false,
    areSuggestedStatusTagsAutoCreated: false,
    isStyleguideOnboardingSeen: false,
    isMissingAssetsWarningSeen: false,
    isTailwindExtensionPromotionSeen: false,
    selectedSidebarTab: SidebarTabType.Activity,
    localExtensionUrl: null,

    styleguideComponentsSortType: SortTypes.SECTIONS,
    dashboardSortType: SortTypes.SECTIONS,

    // Android
    androidResFolderReminderSeen: false,

    lastNoteTypeUsed: "None",
    annotationFirstTimeLandingHidden: false,
    lastRemarkTypeUsed: RemarkType.Dot

    // Screen/component prefs
    // [id]: { versionsLastSeen: Date, assetNames: { [assetId]: String } }
};

const ORGANIZATION_DEFAULTS = {
    projectSortType: SortTypes.SECTIONS,
    collapsedSections: {},
    dialogDismissStates: {},
    freeTrialExpirationHintboxSeen: false,
    freeTrialEndedDialogSeen: false,
    transferProjectTooltipSeen: false,
    missingAddressBannerLastSeenDate: null,
    projectLimitTeyzeSeenForCount: null,
    showMoreProjectListExpanded: false
};

let userPrefStore;
let barrelPrefStore;
let extensionPrefStore;
let organizationPrefStore;

function createStores() {
    userPrefStore = localforage.createInstance({
        driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
        name: "userPrefs",
        version: 1.0
    });

    barrelPrefStore = localforage.createInstance({
        driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
        name: "projectPrefs", // TODO: rename this
        version: 1.0
    });

    extensionPrefStore = localforage.createInstance({
        driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
        name: "extensionPrefs",
        version: 1.0
    });

    organizationPrefStore = localforage.createInstance({
        driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
        name: "organizationPrefs",
        version: 1.0
    });
}

createStores();

const userPrefs = {
    getAll() {
        var allPrefs = USER_DEFAULTS;

        return userPrefStore.iterate((value, key) => {
            allPrefs[key] = value;
        }).then(() => allPrefs);
    },
    get(key) {
        return userPrefStore.getItem(key).then(prefValue => (prefValue === null ? USER_DEFAULTS[key] : prefValue));
    },
    set(key, value) {
        return userPrefStore.setItem(key, value);
    },
    remove(key) {
        return userPrefStore.removeItem(key);
    }
};

const barrelPrefs = {
    setQueues: {},
    getAll(bid) {
        return barrelPrefStore.getItem(bid).then(prefs => {
            const result = {
                ...BARREL_DEFAULTS,
                ...prefs,
                // https://zeplin.atlassian.net/browse/WA-1708 because of this bug, we set collapsedSections as undefined in prefs explicitly
                // this will prevent the code to break, and return the default for collapsedSecticons if it's undefined in the prefs
                collapsedSections: (prefs && "collapsedSections" in prefs && !!prefs.collapsedSections) ? prefs.collapsedSections : {}
            };
            return result;
        });
    },
    get(bid, keyOrSid, key) {
        return barrelPrefStore.getItem(bid).then(values => {
            if (!values || !(keyOrSid in values) || (key && !(key in values[keyOrSid]))) {
                if (key) {
                    return BARREL_DEFAULTS[key];
                }

                return BARREL_DEFAULTS[keyOrSid];
            }

            // https://zeplin.atlassian.net/browse/WA-1708 because of this bug, we set collapsedSections as undefined in prefs explicitly
            // this will prevent the code to break, and return the default for collapsedSecticons if it's undefined in the prefs
            if (values && keyOrSid === "collapsedSections" && "collapsedSections" in values && !values.collapsedSections) {
                return BARREL_DEFAULTS.collapsedSections;
            }

            if (!key) {
                return values[keyOrSid];
            }

            return values[keyOrSid][key];
        });
    },
    set(bid, keyOrSid, valueOrKey, value) {
        const setRequest = { keyOrSid, valueOrKey, value };
        if (!barrelPrefs.setQueues[bid]) {
            barrelPrefs.setQueues[bid] = [setRequest];
            setTimeout(() => {
                barrelPrefs.applySetRequests(bid);
            });
        } else {
            barrelPrefs.setQueues[bid].push(setRequest);
        }
    },
    async applySetRequests(bid) {
        const values = await barrelPrefStore.getItem(bid) || {};
        const queue = barrelPrefs.setQueues[bid];
        barrelPrefs.setQueues[bid] = undefined;

        queue.forEach(({ keyOrSid, valueOrKey, value }) => {
            if (!value) {
                values[keyOrSid] = valueOrKey;
            } else {
                if (!values[keyOrSid] || typeof values[keyOrSid] !== "object") {
                    values[keyOrSid] = {};
                }

                values[keyOrSid][valueOrKey] = value;
            }
        });

        barrelPrefStore.setItem(bid, values);
    }
};

const extensionPrefs = {
    get(extensionId, pid, key) {
        return extensionPrefStore.getItem(extensionId).then(prefs => {
            if (!prefs || !(pid in prefs)) {
                return null;
            }

            return key ? prefs[pid][key] : prefs[pid];
        });
    },
    set(extensionId, pid, key, value) {
        return extensionPrefStore.getItem(extensionId).then(prefs => {
            if (!prefs) {
                prefs = {};
            }

            if (!prefs[pid]) {
                prefs[pid] = {};
            }

            prefs[pid][key] = value;
            return extensionPrefStore.setItem(extensionId, prefs);
        });
    },
    remove(extensionId) {
        return extensionPrefStore.removeItem(extensionId);
    }
};

const organizationPrefs = {
    getAll(oid) {
        return organizationPrefStore.getItem(oid)
            .then(prefs => ({ ...ORGANIZATION_DEFAULTS, ...prefs }));
    },
    get(oid, keyOrSecondaryKey, key) {
        return organizationPrefStore.getItem(oid).then(prefs => {
            if (!prefs || !(keyOrSecondaryKey in prefs) || (key && !(key in prefs[keyOrSecondaryKey]))) {
                if (key) {
                    return ORGANIZATION_DEFAULTS[key];
                }

                return ORGANIZATION_DEFAULTS[keyOrSecondaryKey];
            }

            if (!key) {
                return prefs[keyOrSecondaryKey];
            }

            return prefs[keyOrSecondaryKey][key];
        });
    },
    set(oid, keyOrSecondaryKey, valueOrKey, value) {
        return organizationPrefStore.getItem(oid).then(prefs => {
            if (!prefs) {
                prefs = {};
            }

            if (typeof value === "undefined") {
                prefs[keyOrSecondaryKey] = valueOrKey;
                return organizationPrefStore.setItem(oid, prefs);
            }

            if (!prefs[keyOrSecondaryKey]) {
                prefs[keyOrSecondaryKey] = {};
            }

            prefs[keyOrSecondaryKey][valueOrKey] = value;
            return organizationPrefStore.setItem(oid, prefs);
        });
    }
};

function recoveringStoreOp(storeOpFn) {
    return function (...args) {
        return storeOpFn(...args).catch(() => {
            createStores();
            return storeOpFn(...args);
        });
    };
}

barrelPrefs.get = recoveringStoreOp(barrelPrefs.get);
barrelPrefs.applySetRequests = recoveringStoreOp(barrelPrefs.applySetRequests);

userPrefs.get = recoveringStoreOp(userPrefs.get);
userPrefs.set = recoveringStoreOp(userPrefs.set);

extensionPrefs.get = recoveringStoreOp(extensionPrefs.get);
extensionPrefs.set = recoveringStoreOp(extensionPrefs.set);

organizationPrefs.get = recoveringStoreOp(organizationPrefs.get);
organizationPrefs.set = recoveringStoreOp(organizationPrefs.set);

export {
    userPrefs,
    barrelPrefs,
    extensionPrefs,
    organizationPrefs
};
